import React, { useRef } from 'react';

import { HStack, Input, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ScanVaultCentralAccount,
  ScanVaultCentralAccountStatus,
} from 'api/scan-vault/client';
import {
  DetailsIdentifier,
  FormActionErrorMessage,
  StackedHeader,
} from 'components/DataDisplay';
import {
  CancelButton,
  PrimaryButton,
  textFieldStyles,
} from 'components/DataEntry';
import { PauseIcon, PlayIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { CentralScanningCommonFields } from 'containers/Setup/Outpost/Components/CentralScanningCommonFields';
import { OutpostInfoBox } from 'containers/Setup/Outpost/Components/utils';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';
import { actions } from 'containers/Setup/Outpost/slice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  row: ScanVaultCentralAccount;
}

export const DetachAccountModal = (props: Props) => {
  const { isOpen, onClose, row } = props;
  const dispatch = useDispatch();
  const { outpostActions } = useSelector(selectOutpost);
  const [confirmationText, setConfirmationText] = React.useState('');
  const [error, setError] = React.useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    dispatch(
      actions.detachAccount({
        q: {
          accountId: row?.id,
          centralScanningAccountStatusUpdate: {
            status:
              row.status === ScanVaultCentralAccountStatus.Inactive
                ? ScanVaultCentralAccountStatus.Active
                : ScanVaultCentralAccountStatus.Inactive,
          },
        },
        onSuccess: () => {
          onClose();
          dispatch(actions.getAriksaOutposts({ q: {} }));
          setError(false);
        },
        onError: error => {
          setError(true);
        },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setError(false);
        onClose();
      }}
      size="xl"
      initialFocusRef={inputRef}
      header={
        <StackedHeader
          upper={
            (row?.status === ScanVaultCentralAccountStatus.Inactive
              ? 'Restart'
              : 'Stop') + ' Scanning'
          }
          lower={
            row?.status === ScanVaultCentralAccountStatus.Inactive
              ? 'Enabled regions for this account will be scanned for vulnerabilities'
              : 'This account will no longer be scanned for vulnerabilities'
          }
          icon={
            row?.status === ScanVaultCentralAccountStatus.Inactive ? (
              <PlayIcon />
            ) : (
              <PauseIcon />
            )
          }
        />
      }
      body={
        <Stack spacing={4}>
          <CentralScanningCommonFields row={row} />
          <OutpostInfoBox
            w="full"
            px={1}
            pb={2}
            borderColor={confirmationText === 'CONFIRM' ? 'inherit' : 'red.400'}
          >
            <DetailsIdentifier
              label={
                'Type “CONFIRM” below to ' +
                (row.status === ScanVaultCentralAccountStatus.Inactive
                  ? 'restart scanning'
                  : 'detach account from Ariksa Outpost')
              }
              isInline={false}
              value={
                <Input
                  {...textFieldStyles.input}
                  border="none"
                  ref={inputRef}
                  color={
                    confirmationText === 'CONFIRM' ? 'green.400' : 'red.400'
                  }
                  value={confirmationText}
                  onChange={e =>
                    setConfirmationText(e.target.value?.toUpperCase())
                  }
                />
              }
              styles={{ label: { px: 2, fontSize: '15px' } }}
            />
          </OutpostInfoBox>
          {error && (
            <FormActionErrorMessage
              //title={error}
              description={
                'Failed to ' +
                (row?.status === ScanVaultCentralAccountStatus.Inactive
                  ? 'restart'
                  : 'stop') +
                ' scanning'
              }
            />
          )}
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton
            onClick={() => {
              onClose();
            }}
          >
            CANCEL
          </CancelButton>
          <PrimaryButton
            px={6}
            onClick={handleSubmit}
            isDisabled={
              outpostActions.isLoading || confirmationText !== 'CONFIRM'
            }
            isLoading={outpostActions.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
